/**
 * BL-2677 (ADR 070 — Ingest Protocol §§ 1, 2, 5) — the request body of `PUT /api/lots/:id/state`: one lot as one
 * request. The phone sends the lot's id, its final fields, what those fields were when it last looked, the
 * photos' new positions and the photos it removed.
 *
 * Create versus edit is decided by the service at run time (the lot id decides), so this schema checks only
 * what the body alone can say. `fields` is a closed key set built from the existing PATCH and create schemas;
 * the values are judged by whichever lane's own schema applies (the PATCH schema for an edit, the create
 * schema for a create), so a rule is never written twice.
 */
import { z } from '@auctogon/core/zod';
import { MAX_MEDIA_PER_LOT } from '@auctogon/core/inventory';

import { LotCreatePayloadSchema } from './item-form';
import { mediaIdSchema, LOT_MEDIA_INGEST_ID_PATTERN } from './lot-attachments';
import { LotPatchBaseValuesSchema, makeLotPatchRequestSchema } from './lot-patch';

/** The caps on the two lists a request carries (one number each, shared with the phone's sender). */
export const LOT_STATE_WRITE_LIMITS = {
  maxPositions: MAX_MEDIA_PER_LOT,
  maxRemovedMediaIds: MAX_MEDIA_PER_LOT,
} as const;

/** Refusal copy, one string per rule so the schema and its tests share them. */
export const LOT_STATE_WRITE_MESSAGES = {
  clientOpId: 'clientOpId must be a lowercase version 4 UUID',
  tooManyPositions: `Send at most ${LOT_STATE_WRITE_LIMITS.maxPositions} positions`,
  tooManyRemovals: `Send at most ${LOT_STATE_WRITE_LIMITS.maxRemovedMediaIds} removed photo ids`,
  baseValuesWithAuction: 'baseValues belongs to an edit, and an auctionId belongs to a create',
  overwriteWithAuction: 'overwrite belongs to an edit, and an auctionId belongs to a create',
  emptyWrite: 'Send at least one field, position or removed photo id',
  createOnlyField: 'This field can only be set when the lot is created',
  baseValuesOnCreate: 'baseValues belongs to an edit. A new lot has no earlier values to compare against',
  overwriteOnCreate: 'overwrite belongs to an edit. A new lot has no earlier values to overwrite',
  notSetAtCreateInAuction:
    'This field is not saved when a lot is created into an auction. Create the lot, then edit it',
  notSetAtCreate: 'This field is not saved when a lot is created. Create the lot, then edit it',
  auctionOnEdit: 'An auctionId belongs to a create. Move a lot with the lot menu',
} as const;

/** Keys the phone must not send in `fields`: other keys of this body, the retired whole arrays, and staged refs. */
const EXCLUDED_FIELD_KEYS: ReadonlySet<string> = new Set([
  'media',
  'documents',
  'baseValues',
  'overwrite',
  'stagedMedia',
  'stagedDocuments',
  'clientOpId',
  // Media and document changes ride positions and removedMediaIds, so the removal tombstones are written in one place.
  'mediaChanges',
  'documentChanges',
]);

/**
 * The editable vocabulary: every key the PATCH body, the create payload or `baseValues` declares, minus the
 * excluded ones. `status` and `moveToAuctionId` are in none of them, so a closed key set refuses them.
 */
export const LOT_STATE_WRITE_FIELD_KEYS: readonly string[] = [
  ...new Set([
    ...Object.keys(makeLotPatchRequestSchema().shape),
    ...Object.keys(LotCreatePayloadSchema.shape),
    ...Object.keys(LotPatchBaseValuesSchema.shape),
  ]),
].filter((key) => !EXCLUDED_FIELD_KEYS.has(key));

/**
 * Keys only a create takes: the create payload's keys that neither the PATCH body nor `baseValues` declares
 * (for example `internalNotes`). An edit that sends one is refused by the service, so a value is never dropped silently.
 */
export const LOT_STATE_WRITE_CREATE_ONLY_KEYS: readonly string[] = LOT_STATE_WRITE_FIELD_KEYS.filter(
  (key) => !(key in makeLotPatchRequestSchema().shape) && !(key in LotPatchBaseValuesSchema.shape),
);

/**
 * The `fields` keys a create into an auction persists: what the place-in-auction `newLot` row builder writes
 * (`buildLotInsertRow` plus `buildLotCatalogFields`) and the notes it attaches. The other vocabulary keys
 * (make, model, year, VIN, serial number, hours, mileage unit, title status, the title mailing and
 * additional contact groups, the show flags, `increment`) are dropped by that door, so the lane refuses them
 * rather than answer 200 over a value that was never stored. BL-2677 PR 2 finding: this is the web door's own
 * behavior; widening it is a separate decision. Shared with the phone's queue (BL-2681), which sends the rest
 * as an edit after the create lands.
 */
export const AUCTION_CREATE_PERSISTED_KEYS: ReadonlySet<string> = new Set([
  'name', 'description', 'categoryId', 'isTitledItem', 'acreage', 'pricingType', 'startPrice', 'reserve',
  'sellerId', 'titleState', 'salesRepId', 'locationId', 'hasLoadOutFee', 'loadOutFee', 'customFields',
  'taxStatus', 'taxRateId', 'visibleToSeller', 'internalNotes',
]);

/**
 * The `fields` keys a home-lot create persists: the auction set plus the item facts `buildHomeLotRow` writes
 * (make, model, year, VIN, serial number, hours, mileage unit, title status). `visibleToSeller` is in the
 * auction set: `toInventoryCreateInput` forwards it (BUG-2508), so the home door stores it too.
 */
export const HOME_CREATE_PERSISTED_KEYS: ReadonlySet<string> = new Set([
  ...AUCTION_CREATE_PERSISTED_KEYS,
  'make', 'model', 'year', 'vin', 'serialNumber', 'hoursMileage', 'mileageUnit', 'titleStatus',
]);

/** `fields`: the closed key set, values left raw for the lane's own schema to judge (money stays wire-shaped). */
const FieldsSchema = z
  .object(Object.fromEntries(LOT_STATE_WRITE_FIELD_KEYS.map((key) => [key, z.unknown().optional()])))
  .strict();

/** The request body of `PUT /api/lots/:id/state`. */
export const LotStateWriteRequestSchema = z
  .object({
    tenantId: z.string().uuid(),
    /** One per send, reused on every retry of that send. */
    clientOpId: z.string().regex(LOT_MEDIA_INGEST_ID_PATTERN, LOT_STATE_WRITE_MESSAGES.clientOpId),
    /** A create only: absent means an unassigned home lot. */
    auctionId: z.string().uuid().optional(),
    fields: FieldsSchema.optional(),
    /** An edit only: what each edited field held when the phone last looked. */
    baseValues: LotPatchBaseValuesSchema.optional(),
    /** An edit only: skip the base check. */
    overwrite: z.boolean().optional(),
    /** Media id to its new position. An id the lot lacks is ignored by the service. */
    // The key count fails first (`abort`), so an oversized record costs one count, not a per-key validation each.
    positions: z
      .record(z.string(), z.unknown())
      .refine((positions) => Object.keys(positions).length <= LOT_STATE_WRITE_LIMITS.maxPositions, {
        message: LOT_STATE_WRITE_MESSAGES.tooManyPositions,
        abort: true,
      })
      .pipe(z.record(mediaIdSchema, z.number().int().nonnegative()))
      .optional(),
    removedMediaIds: z.array(mediaIdSchema).max(LOT_STATE_WRITE_LIMITS.maxRemovedMediaIds, LOT_STATE_WRITE_MESSAGES.tooManyRemovals).optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.auctionId != null && body.baseValues !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['baseValues'], message: LOT_STATE_WRITE_MESSAGES.baseValuesWithAuction });
    }
    if (body.auctionId != null && body.overwrite !== undefined) {
      ctx.addIssue({ code: 'custom', path: ['overwrite'], message: LOT_STATE_WRITE_MESSAGES.overwriteWithAuction });
    }
    const hasFields = body.fields !== undefined && Object.keys(body.fields).length > 0;
    const hasPositions = body.positions !== undefined && Object.keys(body.positions).length > 0;
    const hasRemovals = body.removedMediaIds !== undefined && body.removedMediaIds.length > 0;
    if (!hasFields && !hasPositions && !hasRemovals) {
      ctx.addIssue({ code: 'custom', path: ['fields'], message: LOT_STATE_WRITE_MESSAGES.emptyWrite });
    }
  });

/** The parsed body: money bases are `Money`, `fields` values are still the wire shapes. */
export type LotStateWriteRequest = z.output<typeof LotStateWriteRequestSchema>;
